import { Box, Breadcrumbs, Container, Grid, Link, Typography } from "@mui/material"
import React, { FC, ReactNode, useEffect } from "react"
import ChevronRightIcon from '../../icons/ChevronRight';
import DownloadIcon from '../../icons/Download'; // eslint-disable-line @typescript-eslint/no-unused-vars
import UploadIcon from '../../icons/Upload'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { BreadcrumbItem } from "@/types/resource";
import { Helmet } from 'react-helmet-async'
import { useDispatch, useSelector } from "react-redux";
import { getBreadcrumbProviders, readBreadcrumbFromSessionStorage } from "@/reducers/breadcrumbProvider";
import { raiseRestoreBreadcrumbsProviders } from "@/actions/breadcrumbProvider";

export interface ContentProps {
  resourceName?: string;
  pageName: string;
  breadcrumbs?: BreadcrumbItem[];
  mainActions?: ReactNode;
  secondaryActions?: ReactNode;
}

export const Content: FC<ContentProps> = ({ children, ...rest }) => {
  const {
    resourceName,
    pageName,
    breadcrumbs: breadcrumbsProp = [],
    mainActions,
    secondaryActions,
  } = rest

  const location = useLocation()
  const dispatch = useDispatch()
  const bcProviders = useSelector(getBreadcrumbProviders)
  useEffect(() => {
    if (!bcProviders || bcProviders.length === 0) {
      const cachedBc = readBreadcrumbFromSessionStorage()
      if (cachedBc.length > 0) {
        dispatch(raiseRestoreBreadcrumbsProviders())
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const extraBreadcrumbs = 
    !bcProviders ? []
      : bcProviders.filter(p => typeof p.item !== 'function' && p.pathname !== location.pathname).map(p => p.item as BreadcrumbItem) 
  let breadcrumbs = breadcrumbsProp
  if (breadcrumbs.length === 0) {
    breadcrumbs = extraBreadcrumbs
  } else {
    const head = breadcrumbsProp[0]
    const tail = breadcrumbsProp.slice(1);
    [breadcrumbs, ] = [head, ...extraBreadcrumbs, ...tail].reduce((acc, b) => {
      const [arr, bag] = acc
      if (bag[b.path]) { return acc }
      bag[b.path] = true
      arr.push(b)
      return acc
    }, [[] as BreadcrumbItem[], {} as any])
  }

  return (
    <>
    <Helmet>
      <title>{pageName} | OnData</title>
    </Helmet>
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: 'calc(100% - 128px)',
        pt: 2,
        pb: 8,
      }}
    >
      <Container>
        <Grid container justifyContent="space-between"
          spacing={3}
        >
          <Grid item>
            <Typography color="textPrimary" variant="h5" >
              {pageName}
            </Typography>
            {breadcrumbs.length === 0 ? null :
            <Breadcrumbs aria-label="breadcrumb"
              separator={<ChevronRightIcon fontSize="small" />}
              sx={{ mt: 1 }}
            >
              {breadcrumbs.map(b =>
                <Link variant="subtitle2" color="textPrimary" underline="hover"
                  key={b.label}
                  component={RouterLink}
                  to={b.locationDescriptor ?? b.path}
                >
                  {b.label}
                </Link>
              )}
              <Typography color="textSecondary" variant="subtitle2" >
                {resourceName}
              </Typography>
            </Breadcrumbs>}
            {secondaryActions &&
              <Box
                sx={{
                  mb: -1, mx: -1, mt: 1,
                  '& > .MuiButton-root': { m: 1 },
                }}
              >
                {secondaryActions}
              </Box>}
          </Grid>
          <Grid item
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              mb: secondaryActions ? '52px' : 0,
            }}
          >
            <Box sx={{ 
              m: -1,
              '& > button': { m: 1 },
            }} >
              {mainActions}
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          {children}
        </Box>
      </Container>
    </Box>
    </>
  )
}


